/*
 * Size-order backstop (Andrew 2026-10-24; rules extended 2026-10-25: Regular/Standard, numeric Tall/Plus/Short, units, hyphen combos, " | " values). A JS port of the PIM sorter, sba-dashboard services/size_order.py
 * (size_sort_key / sort_sizes / _signed_numbers) — that file is the source of truth for the rules; keep the two in
 * step. Shopify can't hold a size order on "sparse grid" products (not every color has every size), so the
 * pickers sort the Size buttons on the page.
 *
 * Refuses rather than guesses: sortSizes() returns null when ANY value is unrecognised or the list mixes letter
 * and numeric scales — the caller then keeps Shopify's order. Equal ranks keep their input order (stable sort).
 * Parity tests: tests/size-order.test.js (fixtures generated by the Python sorter).
 *
 * Browser: window.SBASizeOrder.sortSizes(values) and .reorder(container, buttonSelector, valueAttr).
 */
(function (root) {
  'use strict';

  var BASE = { SMALL: 'S', SM: 'S', S: 'S', MEDIUM: 'M', MED: 'M', MD: 'M', M: 'M', LARGE: 'L', LG: 'L', L: 'L' };
  var SINGLE = /^(\d)?(X*)(SMALL|SM|S|MEDIUM|MED|MD|M|LARGE|LG|L)$/;
  // Words a numeric size may carry; any other word makes it unrecognised ("Left -1.0" is a diopter, not a size).
  var NUM_WORDS = { MEN: 1, MENS: 1, WOMEN: 1, WOMENS: 1, MAN: 1, LADY: 1, US: 1, EU: 1, UK: 1,
    INCH: 1, INCHES: 1, IN: 1, L: 1, CM: 1, MM: 1,
    // units (2026-10-25): "10 lb", "75Ft", "15 Meter", "2 Pack", '2" x 4"'
    LB: 1, LBS: 1, FT: 1, FOOT: 1, FEET: 1, M: 1, METER: 1, METERS: 1, OZ: 1, KG: 1,
    PACK: 1, PACKS: 1, POCKET: 1, POCKETS: 1, X: 1 };
  var MODIFIER = /^([\s\S]*?)[\s\-]+(X[\s\-]?SHORT|SHORT|TALL|PLUS)$/i;
  // Modifier offsets by scale. Letters: "2X-Small X-Short" < "… Short" < "2X-Small" < "… Tall".
  // Numbers: "6 Short" < "6" < "6 Tall" ("6T") < "6 Plus" ("6+").
  var LETTER_MOD = { XSHORT: -0.2, SHORT: -0.1, TALL: 0.1 };
  var NUMBER_MOD = { SHORT: -0.1, TALL: 0.1, PLUS: 0.2 };
  var NUM_SUFFIX = /^(\d+(?:\.\d+)?)\s*(T|\+)$/i; // "4T", "8+"
  // "Regular" / "Standard": the vendor middle size, between Medium and Large. Only as the WHOLE value.
  var MIDDLE = { REGULAR: 1, STANDARD: 1 };
  // A trailing note ("XL (11-13)", "44 cm (V-Pro 60)") is dropped for the sort parse (letter and numeric).
  var NOTE = /\s*\([^)]*\)$/;
  var PIPE = ' | ';
  // A dash right before a number is a MINUS SIGN unless it follows a number ("4.5-5.5" is a range).
  var DASH = /[-−–]\s*(?=[\d#])/g;

  function isDigit(ch) { return /^\d$/.test(ch); }
  // Python str.strip(chars): strip any of `chars` from both ends.
  function strip(s, chars) {
    var a = 0, b = s.length;
    while (a < b && chars.indexOf(s[a]) !== -1) a++;
    while (b > a && chars.indexOf(s[b - 1]) !== -1) b--;
    return s.slice(a, b);
  }
  var WS = ' \t\n\r\f\v';

  // The numeric SORT parse's minus rule (size_order._is_minus with strict=True): never after a number (a range);
  // otherwise only at the start, after whitespace or "(" — "US-10" and "Mens-8" are 10 and 8.
  function isMinus(text, at) {
    var raw = text.slice(0, at);
    var before = raw.replace(/\s+$/, '');
    if (before && (isDigit(before[before.length - 1]) || before[before.length - 1] === '#')) return false;
    return !raw || /\s/.test(raw[raw.length - 1]) || raw[raw.length - 1] === '(';
  }

  function signedNumbers(value) {
    var text = String(value), out = '', last = 0, m;
    DASH.lastIndex = 0;
    while ((m = DASH.exec(text)) !== null) {
      if (isMinus(text, m.index)) { out += text.slice(last, m.index) + '~'; last = m.index + m[0].length; }
    }
    out += text.slice(last);
    return (out.match(/~?\d+(?:\.\d+)?/g) || []).map(function (n) {
      return n[0] === '~' ? -parseFloat(n.slice(1)) : parseFloat(n);
    });
  }

  // Rank of ONE letter size: XXS -1, XS 0, S 1, M 2, L 3, XL 4, 2XL 5...
  function single(s) {
    var c = s.toUpperCase().replace(/[\s\-.]/g, '').replace(/EXTRA/g, 'X');
    var m = /^(\d)X$/.exec(c);
    if (m) return 3 + parseInt(m[1], 10);
    m = SINGLE.exec(c);
    if (!m) return null;
    var n, base = BASE[m[3]];
    if (m[1]) {
      if (m[2].length !== 1) return null; // "2XL" ok, "5L" is litres
      n = parseInt(m[1], 10);
    } else {
      n = m[2].length;
    }
    if (base === 'S') return 1 - n;
    if (base === 'M') return n === 0 ? 2 : null;
    return 3 + n;
  }

  function pair(parts) {
    if (parts.length !== 2) return null;
    var a = single(parts[0]), b = single(parts[1]);
    return a === null || b === null ? null : (a + b) / 2;
  }

  // A letter size or a two-size combo ("S/M", "Medium-Large", "Large - X-Large").
  function letter(s) {
    s = strip(strip(strip(s, WS), ','), WS);
    if (s === 'ML') return 2.5; // the BARE / Aqua Lung Medium-Large
    // "S-M" is the S/M combo; "X-Large" / "2X-Large" stay singles.
    var parts = s.split(/\s*-\s*/).map(function (p) { return strip(p, WS); });
    if (parts.length === 2 && parts.every(function (p) { return p.length >= 1 && p.length <= 3; })) {
      var r2 = pair(parts);
      if (r2 !== null) return r2;
    }
    var r = single(s);
    if (r !== null) return r;
    var seps = [/\s*\/\s*/, /\s+-\s+/, /-/, /\s+/];
    for (var i = 0; i < seps.length; i++) {
      var ps = s.split(seps[i]).map(function (p) { return strip(p, ' ,'); }).filter(Boolean);
      if (ps.length === 2) {
        var rp = pair(ps);
        if (rp !== null) return rp;
      }
    }
    // "X-Large-2X-Large", "Large-X-Large": a half has its own hyphen — the first split point where both halves
    // are single sizes.
    if ((s.match(/-/g) || []).length >= 2 && !/\s/.test(s)) {
      for (var j = 0; j < s.length; j++) {
        if (s[j] !== '-') continue;
        var a = single(s.slice(0, j)), b = single(s.slice(j + 1));
        if (a !== null && b !== null) return (a + b) / 2;
      }
    }
    return null;
  }

  function numeric(value) {
    var v = strip(value, WS);
    var m = NUM_SUFFIX.exec(v);
    if (m) return [parseFloat(m[1]) + NUMBER_MOD[m[2].toUpperCase() === 'T' ? 'TALL' : 'PLUS']]; // "4T", "8+"
    m = /^EU\s*(\d+(?:\.\d+)?)/i.exec(v); // "EU 38/39 | US Man 6/7"
    if (m) return [parseFloat(m[1])];
    var nums = signedNumbers(v);
    if (!nums.length || nums.length > 2) return null;
    // Inch marks: '' " ” ″. A lone ' is FEET — left unrecognised.
    var words = v.replace(/''|["”″]/g, ' ').match(/[A-Za-z']+/g) || [];
    for (var i = 0; i < words.length; i++) {
      if (!NUM_WORDS[words[i].replace(/'/g, '').toUpperCase()]) return null;
    }
    return nums;
  }

  // [kind, ranks]: kind 0 = letter scale, 1 = numeric. null = unrecognised.
  function sizeSortKey(value) {
    if (value == null || !String(value).trim()) return null;
    var v = strip(String(value), WS);
    if (MIDDLE.hasOwnProperty(strip(v.replace(NOTE, ''), WS).toUpperCase())) return [0, [2.5]];
    // "S/M | US Kid 9.5/13.5 | EU 26/32": the first segment is the size (values starting with EU keep the EU rule).
    if (v.indexOf(PIPE) !== -1 && !/^EU\b/i.test(v)) {
      var rp = letter(v.split(PIPE)[0]);
      if (rp !== null) return [0, [rp]];
    }
    var lv = strip(v.replace(NOTE, ''), WS) || v;
    var m = MODIFIER.exec(lv);
    if (m) {
      var word = m[2].replace(/[\s\-]/g, '').toUpperCase();
      var r = LETTER_MOD.hasOwnProperty(word) ? letter(m[1]) : null;
      if (r !== null) return [0, [r + LETTER_MOD[word]]];
      var n = NUMBER_MOD.hasOwnProperty(word) ? numeric(m[1]) : null;
      if (n !== null && n.length === 1) return [1, [n[0] + NUMBER_MOD[word]]];
      return null;
    }
    var rl = letter(lv);
    if (rl !== null) return [0, [rl]];
    var nn = numeric(lv);
    return nn !== null ? [1, nn] : null;
  }

  // Python tuple ordering on [kind, ranks].
  function cmp(a, b) {
    if (a[0] !== b[0]) return a[0] - b[0];
    var x = a[1], y = b[1];
    for (var i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i] < y[i] ? -1 : 1;
    return x.length - y.length;
  }

  // `values` in size order, or null if any is unrecognised / scales mix.
  function sortSizes(values) {
    if (!values || !values.length) return null;
    var keys = values.map(sizeSortKey);
    if (keys.some(function (k) { return k === null; })) return null;
    if (keys.some(function (k) { return k[0] !== keys[0][0]; })) return null;
    return values.map(function (v, i) { return { v: v, k: keys[i], i: i }; })
      .sort(function (a, b) { return cmp(a.k, b.k) || a.i - b.i; })
      .map(function (p) { return p.v; });
  }

  // Reorder the buttons (direct or nested children of `container`) matching `selector` into size order, by the
  // value in `attr`. Each button's top-level child of `container` is moved, so wrappers (<li>) move with it.
  // Does nothing when the list is refused.
  function reorder(container, selector, attr) {
    if (!container) return false;
    var btns = Array.prototype.slice.call(container.querySelectorAll(selector));
    var vals = btns.map(function (b) { return b.getAttribute(attr); });
    var sorted = sortSizes(vals);
    if (!sorted || sorted.join('\u0000') === vals.join('\u0000')) return false;
    var items = btns.map(function (b) {
      var n = b;
      while (n.parentNode && n.parentNode !== container) n = n.parentNode;
      return n;
    });
    var byVal = {};
    vals.forEach(function (v, i) { (byVal[v] = byVal[v] || []).push(items[i]); });
    sorted.forEach(function (v) { container.appendChild(byVal[v].shift()); });
    return true;
  }

  // Which option is Size-like — the dashboard's rule: the name contains "size", or ≥2 values parse as letter sizes;
  // never an option whose name contains color/colour/style.
  function isSizeOption(name, values) {
    var n = String(name || '').toLowerCase();
    if (/colou?r|style/.test(n)) return false;
    if (n.indexOf('size') !== -1) return true;
    return (values || []).filter(function (v) { var k = sizeSortKey(v); return k && k[0] === 0; }).length >= 2;
  }

  // Every picker on the page. Loaded with `defer` from layout/theme.liquid BEFORE the picker scripts, so the
  // buttons are in size order before any picker reads them (BCD / enriched / dive-computer pickers read button
  // order at init; the legacy jQuery widget runs on document ready, after this).
  function sortPage() {
    var vals = function (els, attr) { return els.map(function (b) { return b.getAttribute(attr); }); };
    // BCD template (snippets/pdp-bcd-variant-picker.liquid)
    document.querySelectorAll('[data-bcd-opt-group]').forEach(function (g) {
      var btns = Array.prototype.slice.call(g.querySelectorAll('[data-option-value]'));
      if (isSizeOption(g.getAttribute('data-option-name'), vals(btns, 'data-option-value'))) {
        reorder(g.querySelector('.pdp-bcd-opt__values'), '[data-option-value]', 'data-option-value');
      }
    });
    // Enriched / dive-computer / fins templates (sections/pdp-product-info.liquid): Size renders as .pdp-size-btn-row
    document.querySelectorAll('[data-pdp-style-cards]').forEach(function (row) {
      var btns = Array.prototype.slice.call(row.querySelectorAll('[data-option-value]'));
      if (isSizeOption(row.getAttribute('data-option-name'), vals(btns, 'data-option-value'))) {
        reorder(row, '[data-option-value]', 'data-option-value');
      }
    });
    // Legacy template (snippets/pdp-options.liquid): <ul class="pdp_size_var"><li data-size-value>…
    document.querySelectorAll('[data-legacy-option-name]').forEach(function (wrap) {
      var ul = wrap.querySelector('ul.pdp_size_var');
      if (!ul) return;
      var lis = Array.prototype.slice.call(ul.querySelectorAll('li[data-size-value]'));
      if (isSizeOption(wrap.getAttribute('data-legacy-option-name'), vals(lis, 'data-size-value'))) {
        reorder(ul, 'li[data-size-value]', 'data-size-value');
      }
    });
  }

  var api = { sizeSortKey: sizeSortKey, sortSizes: sortSizes, reorder: reorder, isSizeOption: isSizeOption };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  root.SBASizeOrder = api;
  try { sortPage(); } catch (e) { /* never block the page over a sort */ }
})(typeof window !== 'undefined' ? window : this);
